import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, 
  X, 
  CheckCircle, 
  Clock, 
  Package,
  RefreshCw
} from 'lucide-react';
import { getAlerts, getUnresolvedAlerts, resolveAlert, deleteAlert } from '../../api/alerts';
import { checkAlerts } from '../../api/chemicals';
import styles from './Alerts.module.scss';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unresolved, resolved
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (filter === 'unresolved') {
        response = await getUnresolvedAlerts();
        setAlerts(response);
        setTotalPages(1);
      } else {
        const params = {
          skip: (currentPage - 1) * itemsPerPage,
          limit: itemsPerPage,
          is_resolved: filter === 'resolved' ? true : filter === 'unresolved' ? false : undefined
        };
        response = await getAlerts(params);
        setAlerts(response);
        // For now, we'll assume we have more pages if we get a full page
        setTotalPages(response.length === itemsPerPage ? currentPage + 1 : currentPage);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, currentPage]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleResolveAlert = async (alertId) => {
    try {
      await resolveAlert(alertId);
      setAlerts(prevAlerts =>
        prevAlerts.map(alert =>
          alert.id === alertId
            ? { ...alert, is_resolved: true, resolved_at: new Date().toISOString() }
            : alert
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (window.confirm('Are you sure you want to delete this alert?')) {
      try {
        await deleteAlert(alertId);
        setAlerts(prevAlerts =>
          prevAlerts.filter(alert => alert.id !== alertId)
        );
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const getAlertIcon = (alert) => {
    if (alert.is_resolved) {
      return <CheckCircle size={20} className={styles.resolvedIcon} />;
    }
    
    if (alert.message.toLowerCase().includes('out of stock')) {
      return <Package size={20} className={styles.criticalIcon} />;
    }
    
    return <AlertTriangle size={20} className={styles.warningIcon} />;
  };

  const getAlertSeverity = (alert) => {
    if (alert.is_resolved) return 'resolved';
    if (alert.message.toLowerCase().includes('out of stock')) return 'critical';
    return 'warning';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCheckAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await checkAlerts();
      alert(`Alert check completed! ${result.alerts_created.length} alerts created.`);
      
      // Reload alerts to show the new ones
      await loadAlerts();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <RefreshCw size={24} className={styles.spinning} />
        <p>Loading alerts...</p>
      </div>
    );
  }

  return (
    <div className={styles.alertsContainer}>
      <div className={styles.header}>
        <h2>Chemical Stock Alerts</h2>
        <div className={styles.controls}>
          <div className={styles.filterButtons}>
            <button
              className={`${styles.filterButton} ${filter === 'all' ? styles.active : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              All Alerts
            </button>
            <button
              className={`${styles.filterButton} ${filter === 'unresolved' ? styles.active : ''}`}
              onClick={() => handleFilterChange('unresolved')}
            >
              Unresolved
            </button>
            <button
              className={`${styles.filterButton} ${filter === 'resolved' ? styles.active : ''}`}
              onClick={() => handleFilterChange('resolved')}
            >
              Resolved
            </button>
          </div>
          <button
            className={styles.checkAlertsButton}
            onClick={handleCheckAlerts}
            title="Check all chemicals for alerts"
          >
            <AlertTriangle size={16} />
            Check Alerts
          </button>
          <button
            className={styles.refreshButton}
            onClick={loadAlerts}
            title="Refresh alerts"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {alerts.length === 0 ? (
        <div className={styles.noAlerts}>
          <CheckCircle size={48} className={styles.noAlertsIcon} />
          <h3>No alerts found</h3>
          <p>
            {filter === 'unresolved' 
              ? 'All chemical stock levels are within acceptable limits.'
              : 'No alerts match your current filter.'
            }
          </p>
        </div>
      ) : (
        <>
          <div className={styles.alertsList}>
            {alerts.map((alert) => {
              const severity = getAlertSeverity(alert);
              return (
                <div
                  key={alert.id}
                  className={`${styles.alertItem} ${styles[severity]}`}
                >
                  <div className={styles.alertIcon}>
                    {getAlertIcon(alert)}
                  </div>
                  
                  <div className={styles.alertContent}>
                    <div className={styles.alertMessage}>
                      {alert.message}
                    </div>
                    <div className={styles.alertMeta}>
                      <div className={styles.alertInfo}>
                        <Clock size={14} />
                        <span>Triggered: {formatDate(alert.triggered_at)}</span>
                        {alert.resolved_at && (
                          <>
                            <CheckCircle size={14} />
                            <span>Resolved: {formatDate(alert.resolved_at)}</span>
                          </>
                        )}
                      </div>
                      <div className={styles.alertThreshold}>
                        Threshold: {alert.threshold} units
                      </div>
                    </div>
                  </div>

                  <div className={styles.alertActions}>
                    {!alert.is_resolved && (
                      <button
                        className={styles.resolveButton}
                        onClick={() => handleResolveAlert(alert.id)}
                        title="Mark as resolved"
                      >
                        <CheckCircle size={16} />
                        Resolve
                      </button>
                    )}
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteAlert(alert.id)}
                      title="Delete alert"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageButton}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                className={styles.pageButton}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Alerts;
